import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import Box from "@mui/material/Box";
import {
	DialogContent, Divider, FormControl, FormControlLabel,
	FormHelperText, FormLabel, Switch, TextareaAutosize,
	Button, Stack, Typography, Link,
} from "@mui/material";
import MainCard from "../../theme/mantis/components/mainCard";
import { ErrorMessage, FormikProvider } from "formik";
import phoneGif from "../../assets/images/phone.gif";
import { useTranslation } from "react-i18next";
import { PAGE_HOME } from "../../locales/pages/namespaces";
import useSupportDialog from "./useSupportDialog";
import { useEffect, useState } from "react";
import PrivacyPolicy from "../privacyPolicy/privacyPolicy";

const SupportDialog = (props) => {
	const { onClose, selectedValue, open } = props
	const { t } = useTranslation(PAGE_HOME)
	const [openModal, setOpenModal] = React.useState(false)
	const handleOpenModal = () => setOpenModal(true);
	const handleCloseModal = () => setOpenModal(false);

	const handleClose = () => {
		onClose(selectedValue);
		formik.resetForm()
	}

	const { formik } = useSupportDialog({ handleClose })

	const textareaStyle = {
		display: 'flex',
		height: '40px',
		padding: '8px 16px',
		flexDirection: 'column',
		alignItems: 'center',
		gap: '10px',
		alignSelf: 'stretch',
		borderRadius: '8px',
		border: ' 0px solid #CFD3D4',
		background: '#F4F5FA',
		overflow: 'auto',
		width: '100%',
		fontSize: '1rem'
	}

	const [zoomLevel, setZoomLevel] = useState(1);

	useEffect(() => {
		const checkZoom = () => {
			const zoom = window.innerWidth / window.outerWidth;
			setZoomLevel(zoom);
		};

		window.addEventListener('resize', checkZoom);
		checkZoom();

		return () => {
			window.removeEventListener('resize', checkZoom);
		};
	}, []);

	return (
		<Dialog
			onClick={(e) => {
				e.stopPropagation()
			}}
			open={open}
			sx={{
				fontFamily: 'IMS, sans-serif !important',
				'& *': {
					fontFamily: 'IMS, sans-serif !important'
				},
				'& .MuiPaper-root': {
					border: '0.6px solid #DAE0F0',
					borderRadius: '12px',
					margin:'0',
				},
				'.MuiBox-root': {
					color: '#485476',
					fontSize: '22px',
					fontWeight: '500'
				},
				p: '0 10px 0 10px',
				'& .MuiCardHeader-root': {
					padding: '20px 20px 0 20px',
					display:'flex',
					flexDirection: 'row',
				},
				'& .MuiCard-root > .MuiDivider-root': {
					display: 'none',
				},
			}}
		>
			<MainCard
				withoutArrow={true}
				sx={{ maxWidth: '500px', overflow: zoomLevel >= 1.5 ? 'scroll' : 'auto', '& .MuiCardHeader-content .MuiTypography-root .MuiBox-root': {fontSize: { xs: '18px', sm: '20px'} } }}
				title={t('Contact us')}
				secondary={
					<Box
						onClick={(e) => {
							e.stopPropagation()
							handleClose()
						}}
						sx={{
							color: '#485476',
							fontSize: '13px !important',
							lineHeight: '14px',
							border: '0.6px solid #485476',
							borderRadius: '50%',
							height: '18px',
							width: '18px',
							textAlign: 'center',
							fontWeight: '600 !important'
						}}
					>
						x
					</Box>
				}
			>
				<Box>
					<DialogContent sx={{ p: 0 }}>
						<FormikProvider value={formik}>
							<Box sx={{ p: 0.5 }}>
								<FormControl
									variant="standard"
									fullWidth
									error={
										formik.touched["Body"] &&
										Boolean(formik.errors["Body"])
									}
									sx={{ mb: 1 }}
								>

									<Typography sx={{ margin: '0 0 24px', fontSize: { xs: '12px'} }} className='color-secondary'>
										{t('Send us your message and a representative from IMS will contact you shortly')}
									</Typography>


									<TextareaAutosize
										placeholder={t('Message')}
										aria-label="maximum height"
										minRows={3}
										value={formik.values.Body}
										onChange={formik.handleChange}
										onBlur={formik.handleBlur}
										name={'Body'}
										style={textareaStyle}
										size={'small'}
										sx={{fontSize: '12px', maxHeight: '180px'}}
									/>
									<FormHelperText>
										<ErrorMessage name={"Body"} />
									</FormHelperText>
								</FormControl>
								<FormControl
									variant="standard"
									fullWidth
									error={
										formik.touched["isInformed"] &&
										Boolean(formik.errors["isInformed"])
									}
									sx={{ pb: 1, m:0 }}
								>
									<FormControlLabel
										sx={{
											'& .MuiFormControlLabel-asterisk': {
												display: 'none',
											},
											margin:'3px 0 4px 0'
										}}
										required control=
										{
											<Switch
												checked={formik.values.isInformed}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												size={'medium'}
												name={'isInformed'}
												sx={{
													'.MuiSwitch-thumb::before': {
														display: 'none'
													},
													'.MuiSwitch-thumb': {
														backgroundColor: 'white'
													},
													'& .MuiSwitch-track': {
														backgroundColor: '#485476',
													}
												}}
											/>
										}
										label={
											<Typography className='p-sm color-default'sx={{
												fontSize: '14px !important', lineHeight: '14px !important'}}>
												{t('I agree with the')}{' '}
												<Button
													onClick={handleOpenModal}
													className='p-sm color-default'
													sx={{
														backgroundColor: 'transparent',
														textDecorationLine: 'underline',
														m: 0,
														p: 0
													}}
												>
													{t('Terms and Policies')}
												</Button>
											</Typography>
										}
									/>
									<FormHelperText>
										<ErrorMessage name={"isInformed"} />
									</FormHelperText>
								</FormControl>
								<Dialog onClose={handleCloseModal} open={openModal}>
									<PrivacyPolicy isModal={true} handleCloseModal={handleCloseModal} />
								</Dialog>

								<Button
									variant={"contained"}
									color={'info'}
									onClick={(event) => {
										event.stopPropagation()
										formik.submitForm()
									}}
									sx={{
										padding: '16px 0',
										mb: '17px',
										width: '100%'
									}}
								>
									{t('Send')}
								</Button>
							</Box>
						</FormikProvider>

						<Divider
							textAlign="center"
							className='color-secondary p-sm'
						>
							{t('or')}
						</Divider>


						<Stack direction={'row'} sx={{ backgroundColor: '#F5F7FC', margin: '18px 0', padding: '8px 0', textAlign: 'center', borderRadius: '12px'}}>

							<Box direction={'column'} sx={{ marginLeft: '0',flexWrap:'nowrap !important', gap:{ xs: '0 !important', sm: '16px !important' }  }} className='centered-wrap'>
								<a
									href="tel:2109520047"
									style={{ textDecoration: 'none', color: '#485776' }}
								>
									<img src={phoneGif} width={80} height={80} color={'#656161'} alt="Call us" />
								</a>

								<Box sx={{ textAlign:'left'}}>
									<Typography
										className='color-secondary title-sm-light'
										sx={{
											textAlign:'left',
											marginBottom:'4px',
											lineHeight:'14px !important',
											fontSize: {
												xs: '14px !important',
											}
										}}
									>
										{t('call us')}
									</Typography>
									<Typography className='color-default p-xl' sx={{textAlign:'left', marginBottom: '4px', lineHeight: '16px !important'}}>
										<Link
											href="tel:2109520047"
											underline="none"
											color="#485776"
											sx={{
												lineHeight: '16px !important',
												fontSize: {
													xs: '16px',
													sm: '16px',
												},
											}}
										>
											210 9520047
										</Link>
									</Typography>
									<Typography className='color-secondary title-sm-light' sx={{
											lineHeight:'14px !important',
											fontSize: '14px !important',
										}}>
										{t('work_hours')}
									</Typography>
								</Box>
								

							</Box>
						</Stack>
					</DialogContent>
				</Box>
			</MainCard>
		</Dialog>
	);
}

export default SupportDialog